import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Clock,
  MapPin,
  CheckCircle,
  XCircle,
  Play,
  Square,
  Timer
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClockInOut() {
  const { user, organization } = useAuth();
  const [clockStatus, setClockStatus] = useState({ clocked_in: false, entry: null });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(false);
  const [location, setLocation] = useState(null);

  useEffect(() => {
    fetchData();
    getLocation();
  }, [user, organization]);

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.log('Location access denied:', error);
        }
      );
    }
  };

  const fetchData = async () => {
    try {
      // Get staff record for current user
      const staffRes = await axios.get(`${API_URL}/api/staff`, {
        params: { organization_id: organization?.id }
      });
      
      const currentStaff = staffRes.data.find(s => s.user_id === user?.id);
      
      if (currentStaff) {
        const [statusRes, entriesRes] = await Promise.all([
          axios.get(`${API_URL}/api/clock/current`, {
            params: { staff_id: currentStaff.id }
          }),
          axios.get(`${API_URL}/api/clock/entries`, {
            params: { staff_id: currentStaff.id, organization_id: organization?.id }
          })
        ]);
        
        setClockStatus(statusRes.data);
        setEntries(entriesRes.data);
      }
    } catch (error) {
      console.error('Error fetching clock data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClockIn = async () => {
    setClockingIn(true);
    try {
      // Get staff record
      const staffRes = await axios.get(`${API_URL}/api/staff`, {
        params: { organization_id: organization?.id }
      });
      const currentStaff = staffRes.data.find(s => s.user_id === user?.id);
      
      if (!currentStaff) {
        toast.error('Staff record not found. Please contact HR.');
        return;
      }

      await axios.post(`${API_URL}/api/clock/in`, {
        staff_id: currentStaff.id,
        organization_id: organization?.id,
        location_lat: location?.lat,
        location_lng: location?.lng,
        location_address: location ? `${location.lat}, ${location.lng}` : null
      });
      
      toast.success('Clocked in successfully!');
      fetchData();
    } catch (error) {
      console.error('Error clocking in:', error);
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    } finally {
      setClockingIn(false);
    }
  };

  const handleClockOut = async () => {
    setClockingIn(true);
    try {
      await axios.post(`${API_URL}/api/clock/out`, {
        entry_id: clockStatus.entry.id,
        location_lat: location?.lat,
        location_lng: location?.lng
      });
      
      toast.success('Clocked out successfully!');
      fetchData();
    } catch (error) {
      console.error('Error clocking out:', error);
      toast.error('Failed to clock out');
    } finally {
      setClockingIn(false);
    }
  };

  const formatDuration = (clockIn, clockOut) => {
    const start = new Date(clockIn);
    const end = clockOut ? new Date(clockOut) : new Date();
    const diff = Math.floor((end - start) / 1000 / 60);
    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;
    return `${hours}h ${minutes}m`;
  };

  const getTodayHours = () => {
    const today = new Date().toDateString();
    let totalMinutes = 0;
    
    entries.forEach(entry => {
      const entryDate = new Date(entry.clock_in).toDateString();
      if (entryDate === today && entry.clock_out) {
        const start = new Date(entry.clock_in);
        const end = new Date(entry.clock_out);
        totalMinutes += Math.floor((end - start) / 1000 / 60);
      }
    });
    
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="min-h-screen" data-testid="clock-page">
      <Header title="Clock In/Out" />
      
      <div className="p-6 space-y-6">
        {/* Main Clock Card */}
        <Card className="overflow-hidden" data-testid="clock-card">
          <div className={`p-8 text-center ${clockStatus.clocked_in ? 'bg-gradient-to-br from-teal-600 to-teal-700' : 'bg-gradient-to-br from-slate-700 to-slate-800'}`}>
            <div className="max-w-md mx-auto">
              <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-6 ${clockStatus.clocked_in ? 'bg-white/20' : 'bg-white/10'}`}>
                <Clock className={`w-12 h-12 ${clockStatus.clocked_in ? 'text-white' : 'text-slate-300'}`} />
              </div>
              
              <h2 className="text-3xl font-bold text-white mb-2">
                {clockStatus.clocked_in ? 'Currently Clocked In' : 'Not Clocked In'}
              </h2>
              
              {clockStatus.clocked_in && clockStatus.entry && (
                <div className="text-white/80 mb-6">
                  <p>Since: {new Date(clockStatus.entry.clock_in).toLocaleTimeString()}</p>
                  <p className="text-2xl font-bold mt-2">
                    {formatDuration(clockStatus.entry.clock_in, null)}
                  </p>
                </div>
              )}
              
              {location && (
                <div className="flex items-center justify-center gap-2 text-white/70 text-sm mb-6">
                  <MapPin className="w-4 h-4" />
                  <span>Location tracked</span>
                  <CheckCircle className="w-4 h-4 text-green-400" />
                </div>
              )}
              
              <Button
                size="lg"
                onClick={clockStatus.clocked_in ? handleClockOut : handleClockIn}
                disabled={clockingIn || loading}
                className={`rounded-full px-12 py-6 text-lg font-semibold transition-transform hover:scale-105 ${
                  clockStatus.clocked_in 
                    ? 'bg-white text-slate-800 hover:bg-slate-100' 
                    : 'bg-teal-500 text-white hover:bg-teal-400'
                }`}
                data-testid="clock-btn"
              >
                {clockingIn ? (
                  <span className="flex items-center gap-2">
                    <Timer className="w-5 h-5 animate-spin" />
                    Processing...
                  </span>
                ) : clockStatus.clocked_in ? (
                  <span className="flex items-center gap-2">
                    <Square className="w-5 h-5" />
                    Clock Out
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Play className="w-5 h-5" />
                    Clock In
                  </span>
                )}
              </Button>
            </div>
          </div>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Today's Hours</p>
                  <p className="text-3xl font-bold text-teal-600">{getTodayHours()}</p>
                </div>
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                  <Timer className="w-6 h-6 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">This Week</p>
                  <p className="text-3xl font-bold">{entries.length} shifts</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Location Status</p>
                  <p className="text-lg font-medium">
                    {location ? (
                      <span className="flex items-center gap-2 text-green-600">
                        <CheckCircle className="w-5 h-5" /> Active
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 text-slate-500">
                        <XCircle className="w-5 h-5" /> Not Available
                      </span>
                    )}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Entries */}
        <Card data-testid="recent-entries">
          <CardHeader>
            <CardTitle>Recent Clock Entries</CardTitle>
          </CardHeader>
          <CardContent>
            {entries.length > 0 ? (
              <div className="space-y-3">
                {entries.slice(0, 10).map((entry) => (
                  <div 
                    key={entry.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        entry.status === 'clocked_in' ? 'bg-green-100' : 'bg-slate-200'
                      }`}>
                        <Clock className={`w-5 h-5 ${
                          entry.status === 'clocked_in' ? 'text-green-600' : 'text-slate-600'
                        }`} />
                      </div>
                      <div>
                        <p className="font-medium">
                          {new Date(entry.clock_in).toLocaleDateString('en-GB', {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short'
                          })}
                        </p>
                        <p className="text-sm text-slate-500">
                          {new Date(entry.clock_in).toLocaleTimeString()} - {
                            entry.clock_out 
                              ? new Date(entry.clock_out).toLocaleTimeString()
                              : 'In progress'
                          }
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant={entry.status === 'clocked_in' ? 'default' : 'secondary'}>
                        {entry.status === 'clocked_in' ? 'Active' : 'Completed'}
                      </Badge>
                      {entry.clock_out && (
                        <p className="text-sm font-medium text-slate-600 mt-1">
                          {formatDuration(entry.clock_in, entry.clock_out)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Clock className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p>No clock entries yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
